import React from 'react';
import { Grid } from '@mentimeter/slides-presentation-ui/grid';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ResponseWithUpvotes } from './ResponseWithUpvotes';
import { Response } from './Response';
import { calculateAdjustedFontSize } from './utils/calculateAdjustedFontSize';
import { getPaginatedResults } from './utils/getPaginatedResults';
import type { OpenEndedResponse } from './types';
import { OpenEndedTitleDataHandler } from './ui/OpenEndedTitleDataHandler';

const NUMBER_OF_COLUMNS = 4;

export const ScreenshotView = ({
  responses,
  maxVoteCount,
  votingOnAnswers,
}: {
  responses: OpenEndedResponse[];
  maxVoteCount: number;
  votingOnAnswers: boolean;
}) => {
  return (
    <Box flex="1 1 auto" width="100%">
      {responses.length > 0 && (
        <Box
          as="section"
          data-testid="responses--screenshot"
          flexDirection="column"
          alignContent="flex-start"
          flexWrap="wrap"
          width="100%"
          mb={5}
        >
          <Grid numberOfColumns={NUMBER_OF_COLUMNS} shouldAnimate={false}>
            {responses.map((item, index) =>
              item.voteCount && votingOnAnswers ? (
                <ResponseWithUpvotes
                  key={item.id}
                  index={index}
                  responseText={item.responseText}
                  participantDisplayName={item.participantDisplayName}
                  upvotes={item.voteCount}
                  onClick={() => {}}
                  isTopVoted={item.voteCount === maxVoteCount}
                  minHeight="300px"
                  height="100%"
                  isTextHidden={false} // Results are always shown in screenshots
                  isReadonly={true}
                />
              ) : (
                <Response
                  index={index}
                  key={item.id}
                  responseText={item.responseText}
                  participantDisplayName={item.participantDisplayName}
                  onClick={() => {}}
                  answerFontSize={calculateAdjustedFontSize(item.responseText)}
                  minHeight="300px"
                  height="100%"
                  isTextHidden={false} // Results are always shown in screenshots
                  shouldAnimate={false}
                  isReadonly={true}
                />
              ),
            )}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export const ScreenshotViewDataHandler = ({
  responses,
  votingOnAnswers,
}: {
  responses: OpenEndedResponse[];
  votingOnAnswers: boolean;
}) => {
  const sortedResults = [...responses].sort(
    (a, b) => b.voteCount - a.voteCount,
  );
  const displayResults = votingOnAnswers ? sortedResults : responses;
  const maxVoteCount = Math.max(...responses.map((v) => v.voteCount));

  const visibleItems = getPaginatedResults(displayResults);

  return (
    <Box
      position="fixed"
      top="0"
      bottom="0"
      left="0"
      right="0"
      px="space16"
      py="space20"
      zIndex={4}
    >
      <OpenEndedTitleDataHandler />
      <ScreenshotView
        responses={visibleItems}
        maxVoteCount={maxVoteCount}
        votingOnAnswers={votingOnAnswers}
      />
    </Box>
  );
};
